import React, { useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Spinner } from 'reactstrap'

import ScreenContainer from 'components/ScreenContainer'

import usePromiseDispatch from 'hooks/usePromiseDispatch'

import { getSharesForRedemptionState } from 'models/General/selectors'
import profileActions from 'models/ProfileInfo/actions'
import { getIsVerifiedUser } from 'models/ProfileInfo/selectors'
import redemptionsActions from 'models/Redemptions/actions'
import {
  getIsRequirementMissing,
  getRedemptionRequirements,
  getRemainingDaysNextRedemption,
} from 'models/Redemptions/selectors'
import rewardsActions from 'models/Rewards/actions'
import { getIsFetchingRewards, getRewards } from 'models/Rewards/selectors'

import { EVENT_TYPE, STRING } from 'resources'
import { APP_URLS } from 'resources/urls'
import { sendAnalyticsEvent } from 'services/analytics'
import WalletScreenHeader from 'screens/Wallet/WalletScreenHeader'

import {
  SpinnerContainer,
  WarningContainer,
  WarningHeader,
  WarningSubHeader,
  WarningIcon,
  SuccessIcon,
} from 'screens/Wallet/styles'
import RedemptionSelection from './RedemptionSelection'
import SharesAvailbilityClosed from './SharesAvailabilityClosed'

import {
  RewardContainer,
  RewardDescription,
  RewardImage,
  RewardName,
  RewardRedemptionValues,
  RewardDescriptionTitle,
  RewardContent,
  RewardDescriptionText,
  RewardImageContainer,
} from './styles'

const SharesRedemption = () => {
  const history = useHistory()
  const promiseDispatch = usePromiseDispatch()

  const isRequirementMissing = useSelector(getIsRequirementMissing)
  const redemptionRequirements = useSelector(getRedemptionRequirements)
  const isVerifiedUser = useSelector(getIsVerifiedUser)
  const remainingDaysNextRedemption = useSelector(getRemainingDaysNextRedemption)
  const isFetchingRewards = useSelector(getIsFetchingRewards)
  const rewards = useSelector(getRewards)
  const sharesForRedemptionState = useSelector(getSharesForRedemptionState)

  const [promptUserRefresh, setPromptUserRefresh] = useState(false)

  const reward = useMemo(() => rewards.find((r) => r.id === 'shares'), [rewards])
  const isSharesEnabled = sharesForRedemptionState === 'on'

  useEffect(() => {
    sendAnalyticsEvent(EVENT_TYPE.redemptionSharesRequirementsView)
  }, [])

  // Fetch profile info & transactions
  useEffect(() => {
    const loadData = async () => {
      const [, tremendousRedemptionOptionsResult] = await Promise.allSettled([
        promiseDispatch(profileActions.fetchProfileInfo.init),
        promiseDispatch(redemptionsActions.fetchTremendousRedemptionOptions.init),
        promiseDispatch(rewardsActions.fetchRedemptionGoal.init),
        promiseDispatch(rewardsActions.fetchAll.init),
      ])

      if (tremendousRedemptionOptionsResult.status === 'rejected') {
        setPromptUserRefresh(true)
      }
    }

    if (sharesForRedemptionState === 'off') {
      history.push(APP_URLS.STORE)
    } else {
      loadData()
    }
  }, [sharesForRedemptionState, history, promiseDispatch])

  let content
  if (isFetchingRewards || !reward || !sharesForRedemptionState) {
    content = (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    )
  } else if (isRequirementMissing) {
    let description = ''
    if (!redemptionRequirements.hasNoOpenRedemptions) {
      description = isVerifiedUser ? STRING.wallet.openRedemption.processing : STRING.wallet.openRedemption.pending
    } else if (
      Object.values(redemptionRequirements).filter((value) => !value).length === 1 &&
      !redemptionRequirements.hasNoRecentRedemptions
    ) {
      description = STRING.wallet.approvedRedemption(remainingDaysNextRedemption)
    }
    return (
      <WarningContainer>
        {description ? <SuccessIcon /> : <WarningIcon />}
        <WarningHeader>
          {description ? STRING.wallet.openRedemption.title : STRING.wallet.notEligible.title}
        </WarningHeader>
        <WarningSubHeader>{description || STRING.wallet.notEligible.description}</WarningSubHeader>
      </WarningContainer>
    )
  } else if (!isSharesEnabled) {
    content = <SharesAvailbilityClosed />
  } else {
    content = (
      <RewardContainer>
        <RewardImageContainer>
          <RewardImage src={reward.imageUrl} />
        </RewardImageContainer>
        <RewardName>{reward.name}</RewardName>
        <RewardRedemptionValues>{`${reward.minShares}+ shares`}</RewardRedemptionValues>
        <RewardContent>
          {!!reward.description && (
            <RewardDescription>
              <RewardDescriptionTitle>{STRING.wallet.rewards.description}</RewardDescriptionTitle>
              <RewardDescriptionText>{reward.description}</RewardDescriptionText>
            </RewardDescription>
          )}
          <RedemptionSelection reward={reward} />
        </RewardContent>
      </RewardContainer>
    )
  }

  return (
    <>
      <WalletScreenHeader promptUserRefresh={promptUserRefresh} />
      <ScreenContainer>{content}</ScreenContainer>
    </>
  )
}

export default SharesRedemption
